import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import { Accordion } from 'react-bootstrap';
import { AccordionEventKey } from 'react-bootstrap/esm/AccordionContext';

import { SearchQueryFilterType } from '@/core/search/model/search';
import { useIsMobile, useClickOutside } from '@/shared/hooks/export';
import {
  searchFiltersTypes,
  SearchContext,
  SearchFilterType,
} from '@/core/search/contexts/search-context';
import { getSearchTypeLabel } from '@/core/search/util/get-search-type-label';
import SearchTypeIcon from '@/core/search/components/search-type-icon';
import { CheckboxRow, MenuList, MenuListItem } from '@/shared/components/export';
import SearchResultsList from '@/core/search/components/search-results-list';
import { getSearchResultTypes } from '@/core/search/services/search-result-type-registry';
import '@/core/search/components/search-results.scss';

export default function SearchResults() {
  const resultsRef = useRef<HTMLDivElement>(null);
  const {
    results,
    filters,
    setFilters,
    setPagination,
    search,
    searchRef,
    modalRef,
    hideSearchResults,
    setHideSearchResults,
    previewed,
    setPreviewed,
    filterType,
  } = useContext(SearchContext)!;

  const isMobile = useIsMobile(991);

  const initialFilters = useMemo(() => {
    if (filterType) return [filterType];
    return searchFiltersTypes;
  }, [filterType]);
  const [searchFiltersTypesActive, setSearchFiltersTypesActive] =
    useState<SearchFilterType[]>(initialFilters);
  const [expandedType, setExpandedType] = useState<SearchFilterType | null>(
    filterType ? filterType : null,
  );
  const [openAccordionKeys, setOpenAccordionKeys] = useState<AccordionEventKey>(
    isMobile ? [] : ['0', '1'],
  );

  useClickOutside(resultsRef, () => setHideSearchResults(true), [searchRef, modalRef]);

  const handleAccordionKeyToggle = (key?: string | string[] | null) => {
    if (!key) return;
    setOpenAccordionKeys(Array.isArray(key) ? key : [key]);
  };

  const toggleTypeFilter = (type: SearchFilterType) => {
    let newTypes = [...(filters.types || [])];

    // article is a special case, if all article types are active, deactivate them all
    if (type === 'article') {
      const articleTypes: SearchQueryFilterType[] = ['story', 'newsletter', 'headline'];
      const hasAllArticleTypes = articleTypes.every(t => newTypes.includes(t));

      if (hasAllArticleTypes) {
        newTypes = newTypes.filter(t => !articleTypes.includes(t));
      } else {
        newTypes = [...newTypes.filter(t => !articleTypes.includes(t)), ...articleTypes];
      }
    } else if (newTypes.includes(type)) {
      newTypes = newTypes.filter(t => t !== type);
    } else {
      newTypes.push(type);
    }
    // reset expanded type when changing filters
    setExpandedType(filterType ? filterType : null);
    setSearchFiltersTypesActive(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      }
      return [...prev, type];
    });
    setFilters({ ...filters, types: newTypes, sortBy: 'date:desc' });
  };

  useEffect(() => {
    setHideSearchResults(false);
    if (search === '') {
      setHideSearchResults(true);
      setSearchFiltersTypesActive(initialFilters);
    }
  }, [search, setHideSearchResults, initialFilters]);

  useEffect(() => {
    setOpenAccordionKeys(isMobile ? [] : ['0', '1']);
  }, [isMobile]);

  useEffect(() => {
    if (filterType) return;
    const totalResultsToShow = 12;
    const activeFiltersCount = searchFiltersTypesActive.length;
    const linesToDisplay = Math.ceil(totalResultsToShow / activeFiltersCount);
    setPagination({ page: 1, pageSize: linesToDisplay });
  }, [setPagination, searchFiltersTypesActive, filterType]);

  useEffect(() => {
    const articleTypes: SearchQueryFilterType[] = ['story', 'newsletter', 'headline'];
    if (expandedType !== null) {
      setPagination({ page: 1, pageSize: 25 });
      if (expandedType === 'article') {
        setFilters({ ...filters, types: articleTypes, sortBy: 'date:desc' });
      } else {
        setFilters({
          ...filters,
          types: [expandedType],
          sortBy: 'date:desc',
        });
      }
    } else {
      let newTypes: SearchQueryFilterType[] = [];
      searchFiltersTypesActive.forEach(f => {
        if (f !== 'article') newTypes.push(f);
        else newTypes = newTypes.concat(articleTypes);
      });
      setFilters({ ...filters, types: newTypes, sortBy: 'date:desc' });
    }
    // eslint-disable-next-line
  }, [expandedType]);

  const typeMenuItems: MenuListItem[] = searchFiltersTypes.map(type => ({
    key: type,
    label: getSearchTypeLabel(type),
    iconElement: <SearchTypeIcon type={type} />,
    selected: !!searchFiltersTypesActive?.includes(type),
    checkboxStyle: true,
    onSelect: () => toggleTypeFilter(type),
  }));

  const searchMenuItems: MenuListItem[] = [
    {
      key: 'matchAll',
      label: 'Match all terms',
      selected: !!filters.matchAll,
      checkboxStyle: true,
      onSelect: () => setFilters({ ...filters, matchAll: !filters.matchAll }),
    },
  ];

  const filtersEl = isMobile ? (
    <div className="p-3 w-100 d-flex gap-2 justify-content-center">
      {!filterType && (
        <MenuList
          trigger={<span className="fw-bold">Type</span>}
          items={typeMenuItems}
          toggleClassName="btn btn-outline-primary-500 btn-sm flex-fill"
          align="left"
        />
      )}
      <MenuList
        trigger={<span className="fw-bold">Search</span>}
        items={searchMenuItems}
        toggleClassName="btn btn-outline-primary-500 btn-sm flex-fill"
        align="right"
      />
    </div>
  ) : (
    <div className="p-3 w-25 border-end d-flex justify-content-center">
      <Accordion
        activeKey={openAccordionKeys}
        alwaysOpen
        onSelect={handleAccordionKeyToggle}
        style={{ width: '180px' }}
      >
        {!filterType && (
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <h3>Type</h3>
            </Accordion.Header>
            <Accordion.Body className="d-flex flex-column gap-2">
              {searchFiltersTypes.map(type => (
                <CheckboxRow
                  key={type}
                  checked={!!searchFiltersTypesActive?.includes(type)}
                  onChange={() => toggleTypeFilter(type)}
                >
                  <SearchTypeIcon type={type} />
                  <span className="fs-5">{getSearchTypeLabel(type)}</span>
                </CheckboxRow>
              ))}
            </Accordion.Body>
          </Accordion.Item>
        )}
        <Accordion.Item eventKey={filterType ? '0' : '1'} className={'mt-4'}>
          <Accordion.Header>
            <h3>Search</h3>
          </Accordion.Header>
          <Accordion.Body className="d-flex flex-column gap-2">
            <CheckboxRow
              checked={!!filters.matchAll}
              onChange={() => setFilters({ ...filters, matchAll: !filters.matchAll })}
            >
              <span className="fs-5">Match all terms</span>
            </CheckboxRow>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );

  const resultsEl = (() => {
    // filter results based on active filters
    const filterResults = (type: SearchFilterType) => {
      const articleTypes: SearchQueryFilterType[] = ['story', 'newsletter', 'headline'];
      if (type === 'article') {
        return results.filter(r => r.types.some(t => articleTypes.includes(t)));
      }
      if (type === 'event') {
        const filtered = results.filter(
          r => r.types.includes(type) && !r.types.some(t => articleTypes.includes(t)),
        );
        // When sorting by date, order events by proximity to now
        if (filters.sortBy && filters.sortBy.startsWith('date:')) {
          const now = Date.now();
          return [...filtered].sort((a, b) => {
            const at = a.date ? new Date(a.date).getTime() : Number.POSITIVE_INFINITY;
            const bt = b.date ? new Date(b.date).getTime() : Number.POSITIVE_INFINITY;
            const ad = Math.abs(at - now);
            const bd = Math.abs(bt - now);
            return ad - bd;
          });
        }
        return filtered;
      }
      return results.filter(
        r => r.types.includes(type) && !r.types.some(t => articleTypes.includes(t)),
      );
    };
    // amount of lines to display for each type depending on the amount of active filters
    const totalResultsToShow = 12;
    const activeFiltersCount = searchFiltersTypesActive.length;
    const linesToDisplay = Math.ceil(totalResultsToShow / activeFiltersCount);

    return (
      <ul
        className="w-100 search-results-list list-group p-3"
        style={isMobile ? { maxHeight: '40vh', overflow: 'auto' } : { maxHeight: '75vh' }}
      >
        {searchFiltersTypesActive.map(type => {
          const typeResults = filterResults(type);
          if (!typeResults.length) return <React.Fragment key={type}></React.Fragment>;
          return (
            <React.Fragment key={type}>
              {(expandedType === null || expandedType === type) && (
                <li className="mb-3 list-group-item border-0 bg-primary-700 h-100 py-0">
                  <SearchResultsList
                    type={type}
                    filterType={filterType}
                    results={typeResults}
                    expand={expandedType === type}
                    setExpandedType={setExpandedType}
                    linesToDisplay={linesToDisplay}
                  />
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ul>
    );
  })();

  return (
    <>
      {!search.length || hideSearchResults ? (
        <></>
      ) : !results.length ? (
        <div
          className="position-fixed w-100 hv-25 z-3 bg-primary-700 p-3"
          style={{ top: 'var(--search-results-top, 56px)' }}
        >
          <h3>No search results</h3>
        </div>
      ) : (
        <div
          ref={resultsRef}
          className={`position-fixed search-results w-100 z-3 bg-primary-700 ${isMobile ? 'd-block' : 'd-block d-lg-flex'}`}
          style={{ maxHeight: '75vh', top: 'var(--search-results-top, 56px)' }}
        >
          {filtersEl}
          {resultsEl}
        </div>
      )}
      {getSearchResultTypes().map(([type, { Preview }]) =>
        Preview ? (
          <Preview
            key={type}
            id={previewed?.type === type ? previewed.id : undefined}
            onClose={() => setPreviewed(undefined)}
          />
        ) : null,
      )}
    </>
  );
}
